import React, { useMemo } from 'react';
import {
    Box, Typography, Alert, Button, Skeleton, CircularProgress,
} from '@mui/material';
import { IPedido } from '../../../../../interface/IPedido';
import { StatusPedido, StatusPedidoDescricao } from '../../../../../enum/StatusPedido';
import { useKDSPorPedido } from '../../../../../../cozinha/hooks/useKDSPorPedido';
import { useEstacoesKDS } from '../../../../../../cozinha/hooks/useEstacoesKDS';
import { MiniKDSEstacao } from '../MiniKDSEstacao';
import { StatusItemKDS } from '../../../../../../cozinha/interfaces/IKDS';

interface MiniKDSContentProps {
    pedido: IPedido;
}

const isEmPreparo = (status: IPedido['status']) =>
    status === StatusPedido['Em preparo'] || status === StatusPedidoDescricao['Em preparo'];

const STATUSES_VISIVEIS = [
    StatusItemKDS.AGUARDANDO,
    StatusItemKDS.EM_PREPARO,
    StatusItemKDS.PRONTO,
    StatusItemKDS.AGUARDANDO_DISPARO,
];

export const MiniKDSContent: React.FC<MiniKDSContentProps> = ({ pedido }) => {
    const {
        loading, loadingAcao, erro,
        itensPorEstacao, temItensAtivos,
        refresh, iniciarItem, concluirItem, reverterItem, concluirTodosItensPedido,
    } = useKDSPorPedido(pedido.uuid);

    const { estacoes, loading: loadingEstacoes } = useEstacoesKDS();

    const readOnly = !isEmPreparo(pedido.status);

    const estacoesComItens = useMemo(() => {
        return estacoes
            .filter(e => {
                const itens = itensPorEstacao.get(e.uuid) ?? [];
                return itens.some(i => STATUSES_VISIVEIS.includes(i.status));
            })
            .sort((a, b) => a.ordemExibicao - b.ordemExibicao);
    }, [estacoes, itensPorEstacao]);

    if (loading || loadingEstacoes) {
        return (
            <Box sx={{ px: 2, pt: 1 }}>
                {[0, 1, 2].map(i => (
                    <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="60%" height={28} />
                        <Skeleton variant="rounded" height={60} sx={{ mt: 0.5 }} />
                    </Box>
                ))}
            </Box>
        );
    }

    if (erro) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" action={
                    <Button size="small" onClick={refresh}>Tentar novamente</Button>
                }>
                    {erro}
                </Alert>
            </Box>
        );
    }

    if (!temItensAtivos) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">
                    Este pedido não possui itens KDS ativos.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2, pt: 1, pb: 8, position: 'relative' }}>
            {loadingAcao && (
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                    <CircularProgress size={18} />
                </Box>
            )}

            {readOnly && (
                <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                    Somente leitura — pedido não está em preparo.
                </Alert>
            )}

            {estacoesComItens.map(estacao => (
                <MiniKDSEstacao
                    key={estacao.uuid}
                    estacao={estacao}
                    itens={(itensPorEstacao.get(estacao.uuid) ?? []).filter(i => STATUSES_VISIVEIS.includes(i.status))}
                    pedidoUuid={pedido.uuid}
                    readOnly={readOnly}
                    loadingAcao={loadingAcao}
                    onIniciar={iniciarItem}
                    onConcluir={concluirItem}
                    onReverter={reverterItem}
                    onConcluirTodos={concluirTodosItensPedido}
                />
            ))}
        </Box>
    );
};
